import { useEffect, useRef, useState, type MutableRefObject, type ReactNode } from "react";
import clsx from "clsx";
import { Check, Download, FileText, Pencil, Trash2, X } from "lucide-react";
import { AcDialog, AddRow, Button, Card, EmptyState, Field, FormRow, Icon, IconButton, Page, Picker, Skeleton, Textarea } from "@cntrl/ui";
import { useCapability, useIsAdvancePro } from "../../app/bandContext.js";
import { ApiError, errorMessage } from "../../app/hooks/apiError.js";
import { pushToast } from "../../app/toasts.js";
import { mediaKindForFile, useMediaDownload, useUploadMedia } from "../../app/hooks/useMedia.js";
import {
  useBandCustomMics, useBankCustomMic, useMics, useProduction, useUpdateProduction,
  type LookId, type MicView, type ProductionFileView, type ProductionRead,
} from "../../app/hooks/useProduction.js";
import { AdvanceCatalogue } from "./advance/AdvanceCatalogue.js";
import { AdvanceLookSwitcher } from "./AdvanceLookSwitcher.js";
import { AdvanceLinkPill } from "./AdvanceLinkPill.js";
import { MicSuggest, type MicOption } from "./MicSuggest.js";
import styles from "./ProductionScreen.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

const MAX_CHANNELS = 128;
const MAX_RIDER = 100;

// The SEVEN production-file categories are the backend `ck_production_files_category` enum — the SOURCE OF
// TRUTH for which kinds exist. The CD `cd-assets` brief lays these out as a grid of per-category "Production assets"
// cards; its console tags ("GrandMA3"/"DiGiCo"/"Kemper") are illustrative sample data carried by each file's free-text
// LABEL, not a separate field (kind-reconciliation, — CD owns layout, the endpoint owns which fields exist).
type CategoryDef = { value: string; title: string; sub?: string; emptyText: string };

// The five preset/backup families → the narrow side column of the CD prod-split (beside the input list).
// TITLES are the spec's shipped strings (SME TASK-018, "check 2nd photo for right names" — the specimen and
// TASK-018-2 agree verbatim). The `value` strings are the `ck_production_files_category` CHECK constraint and
// must never move: a rename here is display-only, no migration.
const PRESET_CATEGORIES: readonly CategoryDef[] = [
  { value: "lighting_preset", title: "Lighting Show File", emptyText: "No lighting show file yet." },
  { value: "foh_preset", title: "FOH Show File", emptyText: "No FOH show file yet." },
  { value: "iem_preset", title: "Monitor presets (IEMs)", emptyText: "No monitor presets (IEMs) yet." },
  { value: "amp_backup", title: "Backline Files", emptyText: "No backline files yet." },
  { value: "master_tracks", title: "Playback Files", emptyText: "No playback files yet." },
] as const;

// Stage plot → the SIXTH card in the preset rail (SME TASK-018 overrides legacy, which put it full-width below
// the split); the full rider document → inside the Hospitality/rider card.
const STAGE_PLOT: CategoryDef = {
  value: "stage_plot", title: "Stage plot",
  emptyText: "No stage plot yet.",
};

/**
 * Assets → Production (shots 34-35), rebuilt to the CD `cd-assets` "Production assets" grid. One
 * whole-document editor: production files grouped into per-category upload cards (presets / amp backups / master tracks
 * / stage plot / full rider, each with a venue-visibility toggle), the input list (auto-numbered channels, a mic picker
 * from the global catalog + band customs, TRAVELING WITH / NEED), tech notes, rider essentials, and the production
 * contact — plus the rotating venue-advance link behind the public /venue/:token/:slug page. The whole surface is
 * production:write-gated (NOT assets:write — the production crew hold assets=venue_only yet own the pack
 * ). The pack is band-shared (a stand-in FOH engineer pulls the same files).
 */
export function ProductionScreen() {
  // The pack is BAND-SHARED read ("others read"): production:read is a wider audience than production:write
  // (a stand-in FOH engineer / booking / content seat views the same pack). Only production:write seats edit it
  // (Upload/Save + the venue-token card). Merch (assets=none, no production:read) gets the role-denied empty.
  const canRead = useCapability("production:read");
  const canWrite = useCapability("production:write");

  // THE Pro check for this whole surface (ART-146). One section-level test, at the route's own screen —
  // never per-widget, never a forked component below this line ("the VENUE_KEYS lesson: scattered logic
  // drifts"). Pro REPLACES the classic stack with the catalogue and NEVER renders both: they are two readings
  // of the same `artist.advance_looks` rows, so showing them together would be one band's advances described
  // twice, in two vocabularies, with two sets of controls over one link.
  // ART-353: the band can switch its team back to the standard page in Settings › Team, so this reads the
  // composed "Pro advance" answer rather than the tier alone.
  const isPro = useIsAdvancePro();

  if (!canRead) {
    return (
      <Page>
        <EmptyState
          title="Production isn't available for your role"
          description="The tech pack is shared with the band's touring seats. Ask a band owner for access."
        />
      </Page>
    );
  }

  // The catalogue owns its own header and lead — a Pro advance is a row in a list, not a single pack, so the
  // page it opens on says something different.
  if (isPro) return <AdvanceCatalogue canWrite={canWrite} />;

  return <StandardAdvance canWrite={canWrite} />;
}

/**
 * The Standard advance page. WHICH ADVANCE is open lives HERE (null = Regular), above the page header, because the
 * look switcher sits in the header's right slot (ART-251) while the unsaved-draft prompt lives in the editor. The
 * editor registers its guarded switch in `guardRef`; the switcher calls through it, so a dirty draft still asks
 * before it is discarded. A reader (no editor mounted) switches straight away.
 */
function StandardAdvance({ canWrite }: { canWrite: boolean }) {
  const [lookId, setLookId] = useState<LookId>(null);
  const guardRef = useRef<((to: LookId) => void) | null>(null);
  const requestLook = (to: LookId) => (guardRef.current ? guardRef.current(to) : setLookId(to));
  // ART-251 (PO): which advance, and its venue link, sit together in the header's right slot on every shell — the
  // separate "Advance Docs Link" card is gone. Mobile: one row, LOOK left / Share + Copy right. Desktop: at the right
  // of the title row, the look switcher then a compact `LINK /venue/…` bar.
  const compact = usePhoneLayout();
  return (
    <Page title="Advance Docs"
      actions={
        <div className={clsx(styles.headerControls, compact && styles.headerControlsFill)}>
          <AdvanceLookSwitcher lookId={lookId} onSelect={requestLook} canWrite={canWrite} />
          {canWrite && <AdvanceLinkPill lookId={lookId} />}
        </div>
      }>
      <ProductionCard canWrite={canWrite} lookId={lookId} onSelectLook={setLookId} guardRef={guardRef} />
    </Page>
  );
}

// ---- shared states --------------------------------------------------------------------------------------------

function LoadingRows() {
  return (
    <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading">
      <Skeleton variant="line" width="60%" />
      <Skeleton variant="line" width="80%" />
      <Skeleton variant="line" width="45%" />
    </div>
  );
}

function ErrorState({ error, onRetry }: { error: unknown; onRetry: () => void }) {
  return (
    <EmptyState
      title="Couldn't load"
      description={error instanceof ApiError ? error.message : "Please try again."}
      action={<Button onClick={onRetry}>Retry</Button>}
    />
  );
}

// ---- the pack editor ------------------------------------------------------------------------------------------

function ProductionCard({ canWrite, lookId, onSelectLook, guardRef }: {
  canWrite: boolean;
  /** WHICH ADVANCE is open (null = Regular) — owned by StandardAdvance; the pack READ is keyed on it. */
  lookId: LookId;
  onSelectLook: (lookId: LookId) => void;
  guardRef: MutableRefObject<((to: LookId) => void) | null>;
}) {
  const setLookId = onSelectLook;
  const q = useProduction(lookId);

  // Error/loading branch ONLY on the initial load; once data exists keep the editor mounted through a FAILED
  // background refetch (the draft-clobber guard) — the multi-section draft lives in ProductionEditor's state.
  if (!q.data) {
    return (
      <Card>
        {q.isError ? <ErrorState error={q.error} onRetry={() => void q.refetch()} /> : <LoadingRows />}
      </Card>
    );
  }
  // Deliberately NOT keyed on the look. Remounting would discard the draft correctly, but it would also unmount
  // the look SWITCHER along with everything else — and with `keepPreviousData` the pack never goes undefined, so
  // the editor reseeds its draft from the arriving pack instead (see `seededFor`).
  return canWrite
    ? <ProductionEditor initial={q.data} lookId={lookId} onSelectLook={setLookId} guardRef={guardRef} />
    : <ReadOnlyProduction pack={q.data} lookId={lookId} onSelectLook={setLookId} />;
}

// ---- production contact ------------------------------------------------------------------------------------------
// The venue link moved OUT of the page body into the header's right slot on every shell (ART-251 — AdvanceLinkPill:
// Share + Copy on the phone, a compact `LINK /venue/…` bar with Copy / Preview / Rotate on desktop), so what remains
// here is the band-level production contact. It persists via the pack's ONE Save.

function ContactCard(props: {
  contactName: string; contactEmail: string; contactPhone: string;
  onContact: (changes: Partial<{ contactName: string; contactEmail: string; contactPhone: string }>) => void;
  /** The pack's shared whole-document Save pill — the contact commits with the rest of the pack. */
  saveBtn?: ReactNode;
}) {
  // `hideLabel` (not `aria-label`): the "Production contact" heading names the group, so each field keeps a real
  // <label> for assistive tech while the row reads as three placeholders. EDITABLE from every advance, including a
  // look — the contact is band-level and the server routes the write to the Regular row. Full-width now, so the
  // three fields sit side by side on desktop and stack on the phone.
  return (
    <Card title="Production contact" actions={props.saveBtn}>
      <div className={styles.contactFields}>
        <Field label="Production contact name" hideLabel value={props.contactName} placeholder="Name"
          onChange={(e) => props.onContact({ contactName: e.target.value })} />
        <Field label="Production contact email" hideLabel value={props.contactEmail} placeholder="Email" inputMode="email"
          onChange={(e) => props.onContact({ contactEmail: e.target.value })} />
        <Field label="Production contact phone" hideLabel value={props.contactPhone} placeholder="Phone" inputMode="tel"
          onChange={(e) => props.onContact({ contactPhone: e.target.value })} />
      </div>
    </Card>
  );
}

// A read-only view of the band-shared pack for production:read (non-write) seats — "others read". No draft, no
// edit affordances, no venue-token card (that GET is production:write-only on the backend). The only specs the
// owner/writer view, so this reader layout shows just the POPULATED surfaces (no empty upload cards) — files grouped by
// category into read-only chips; download stays available (a band-scoped read action).
function ReadOnlyProduction(props: { pack: ProductionRead; lookId: LookId; onSelectLook: (lookId: LookId) => void }) {
  const { pack } = props;
  const compact = usePhoneLayout();
  const { download } = useMediaDownload();
  const files = pack.files ?? [];
  const channels = pack.channels ?? [];
  const rider = pack.notes?.riderEssentials ?? [];
  const hasContact = !!(pack.notes?.productionContactName || pack.notes?.productionContactEmail || pack.notes?.productionContactPhone);
  const isEmpty = files.length === 0 && channels.length === 0 && !pack.notes?.techNotes && rider.length === 0 && !hasContact;

  const onDownload = (mediaId: string, filename: string | null | undefined) =>
    void download(mediaId, filename).catch((e) => pushToast(errorMessage(e, "Couldn't download the file."), "error"));

  // The look switch lives in the page header now (ART-251) — a reader uses the same switcher, minus the
  // create / rename / delete, so a stand-in engineer can still open the FESTIVAL pack.

  if (isEmpty) {
    return (
      <>
        <Card>
          <EmptyState title="No production pack yet" description="The band's touring crew hasn't published a tech pack." />
        </Card>
      </>
    );
  }

  const byCategory = (category: string) => files.filter((f) => (f.category ?? "") === category);

  // Essentials first, then the full-rider ADDITIONS — read top-to-bottom this is the complete rider, read down
  // to the second header it is the essentials. An empty group renders nothing rather than an empty heading.
  const riderGroups = [
    { key: "essential" as const, label: "Essentials", rows: rider.filter((r) => r.tier !== "full") },
    { key: "full" as const, label: "Full rider adds", rows: rider.filter((r) => r.tier === "full") },
  ].filter((g) => g.rows.length > 0);

  const roChip = (f: ProductionFileView, fallback: string) => (
    <div key={f.fileId} className={styles.fileChip}>
      <Icon icon={FileText} size={16} className={styles.fileChipIcon} />
      <div className={styles.fileChipMain}>
        <p className={styles.fileChipName} title={f.label || f.originalFilename || fallback}>{f.label || f.originalFilename || fallback}</p>
        <p className={styles.fileChipMeta}>{roFileMeta(f)}</p>
      </div>
      {f.visibleToVenue && <span className={styles.venueTag}>On venue page</span>}
      {f.mediaId && (
        <button type="button" className={styles.chipBtn} aria-label={`Download ${f.label || f.originalFilename || fallback}`}
          onClick={() => onDownload(f.mediaId!, f.originalFilename ?? f.label)}>
          <Icon icon={Download} size={16} />
        </button>
      )}
    </div>
  );

  // Only the file categories the crew has actually populated (stage plot + the five preset families) — empty cards are
  // noise for a viewer. rider_full renders inside the Hospitality/rider card below.
  const fileCategories = [...PRESET_CATEGORIES, STAGE_PLOT];
  const populated = fileCategories.filter((c) => byCategory(c.value).length > 0);

  return (
    <>
      {channels.length > 0 && (
        <Card id="ro-input" title="Input list">

          {/* Same row pill as the editor, minus the controls — the reader must not fork the visual language. */}
          <div className={styles.inputList}>
            {channels.map((c, i) => (
              <ChannelRow key={i} index={i} instrument={c.instrument ?? ""}
                mic={c.micLabel ?? c.micCustom ?? ""} status={c.status} compact={compact} />
            ))}
          </div>
        </Card>
      )}

      {populated.length > 0 && (
        <div className={styles.roCards}>
          {populated.map((cat) => (
            <article key={cat.value} className={styles.prodCard} aria-label={cat.title}>
              <div className={styles.prodCardHead}>
                <p className={styles.prodCardTitle}>{cat.title}</p>
                {cat.sub && <p className={styles.prodCardSub}>{cat.sub}</p>}
              </div>
              {byCategory(cat.value).map((f) => roChip(f, cat.title))}
            </article>
          ))}
        </div>
      )}

      {pack.notes?.techNotes && (
        <Card id="ro-notes" title="Tech notes">

          <p className={styles.subtitle} style={{ whiteSpace: "pre-line" }}>{pack.notes.techNotes}</p>
        </Card>
      )}

      {(rider.length > 0 || byCategory("rider_full").length > 0) && (
        <Card id="ro-rider" title="Hospitality / rider">

          {/* Grouped the way the PUBLIC VENUE PAGE groups it, so a stand-in engineer reading the pack sees the
              same shape the venue receives: essentials first, then what the full rider adds. The group header
              IS the tag — a per-row "essential" pill would repeat it on every line. */}
          {riderGroups.map((g) => (
            <div key={g.key}>
              <p className={clsx(styles.riderGroup, g.key === "full" && styles.riderGroupFull)}>{g.label}</p>
              {g.rows.map((r, i) => (
                <div key={i} className={styles.riderRowRo}>
                  <span className={styles.strong}>{r.label}</span>
                  {r.value && <span className={styles.subtitle}>{r.value}</span>}
                </div>
              ))}
            </div>
          ))}
          {/* A legacy rider document, if this band ever uploaded one. There is no way to add another. */}
          {byCategory("rider_full").length > 0 && <p className={styles.vlabelSub}>Full rider document (legacy)</p>}
          {byCategory("rider_full").map((f) => roChip(f, "Full rider"))}
        </Card>
      )}

      {hasContact && (
        <Card id="ro-contact" title="Production contact">

          <FormRow>
            {pack.notes?.productionContactName && <span className={styles.strong}>{pack.notes.productionContactName}</span>}
            {pack.notes?.productionContactEmail && <a className={styles.subtitle} href={`mailto:${pack.notes.productionContactEmail}`}>{pack.notes.productionContactEmail}</a>}
            {pack.notes?.productionContactPhone && <a className={styles.subtitle} href={`tel:${pack.notes.productionContactPhone}`}>{pack.notes.productionContactPhone}</a>}
          </FormRow>
        </Card>
      )}
    </>
  );
}

/**
 * The Essential/Full tier on ONE rider row — the specimen's `.rider-tier`, built on the same capsule the Booking
 * page's Confirmed/Holds/Past switcher uses (a track holding two segments, the active one filled with ink) at the
 * row's own type scale, so it sits in a 30px row without inflating it.
 *
 * Two BUTTONS rather than one two-state toggle: the tier is a choice between two named things, and a single
 * `aria-pressed` control would force a screen reader to announce "full: not pressed" for a row that IS an
 * essential. `role="group"` + a per-segment `aria-pressed` states the value the way the control looks.
 *
 * The accessible name carries the ROW, because a card with twelve rider rows otherwise offers twelve identically
 * named "Essential" buttons.
 */
function RiderTierToggle(props: { tier: RiderTier; rowLabel: string; onChange: (tier: RiderTier) => void }) {
  const { tier, rowLabel, onChange } = props;
  return (
    <div className={styles.riderTier} role="group" aria-label={`${rowLabel} tier`}>
      {(["essential", "full"] as const).map((t) => (
        <button key={t} type="button" className={styles.riderTierBtn}
          aria-pressed={tier === t} aria-label={`${rowLabel}: ${t === "full" ? "full rider" : "essential"}`}
          onClick={() => onChange(t)}>
          {t === "full" ? "Full" : "Essential"}
        </button>
      ))}
    </div>
  );
}

// ---- the input-list row (SME TASK-017) --------------------------------------------------------------------------

/** null/"" (no status recorded) cycles to `traveling_with` on the first click; after that it is a two-state flip.
 *  Legacy only ever had the two states — a stored null is a legacy row, and it is NEVER rewritten on load. */
function nextStatus(status: string | null | undefined): string {
  return status === "traveling_with" ? "need" : "traveling_with";
}

/**
 * The chip's copy, and it is NOT the same string in both modes. The editor's chip is a button, so an
 * unrecorded status reads as the instruction it is ("Set status") and the first click picks a side. The
 * READER has no such affordance and no `production:write` seat behind it, so there the same row must read
 * as a VALUE ("—", what the reader showed before this row was shared with the editor) — telling a stand-in
 * engineer to "SET STATUS" puts an instruction in the slot where the data belongs.
 */
function statusText(status: string | null | undefined, opts?: { interactive?: boolean }): string {
  if (status === "traveling_with") return "Traveling with";
  if (status === "need") return "Need from venue";
  return opts?.interactive ? "Set status" : "—";
}

/**
 * The SHARED editable list row (input list + rider). It owns the chrome and the view⇄edit behaviour so the two
 * surfaces cannot drift; the callers fill the slots. Consistent everywhere: a nested-panel card, tap-to-edit on
 * the mobile shell (a pencil on desktop), and one control set — a trashcan DELETE on both platforms, plus,
 * while editing on the mobile shell, a bottom-right footer carrying cancel (✕) and done (✓).
 *
 * Slots — `leading` (e.g. a channel number), `summary` (the view content), `children` (the edit fields),
 * `aside` (a per-row control shown beside the summary: the status pill / the tier toggle). `aside` shows in
 * view on every shell and additionally during edit on DESKTOP (it is a grid column, always present); on the
 * mobile shell it hides while editing so the fields get the row's width.
 *
 * `editBtnRef` is owned by the CALLER (which wires Enter/Escape on its own fields) and attached here to the
 * pencil/check, so a keyboard commit can hand focus to that button before the field unmounts (the two render
 * at the same position, so React keeps the focus through the swap). `onDone`/`onCancel` arrive focus-wrapped.
 */
function EditableRow(props: {
  compact: boolean;
  editing: boolean;
  /** Sets the desktop `grid-template-columns` for this row shape (input vs rider). */
  gridClassName: string;
  editLabel: string;
  cancelLabel: string;
  removeLabel: string;
  editBtnRef: React.RefObject<HTMLButtonElement | null>;
  onEdit: () => void;
  onDone: () => void;
  onCancel: () => void;
  onRemove: () => void;
  leading?: React.ReactNode;
  aside?: React.ReactNode;
  summary: React.ReactNode;
  children: React.ReactNode;
}) {
  const {
    compact, editing, gridClassName, editLabel, cancelLabel, removeLabel, editBtnRef,
    onEdit, onDone, onCancel, onRemove, leading, aside, summary, children,
  } = props;

  const body = editing ? children : summary;
  const remove = (
    <IconButton icon={Trash2} label={removeLabel} size="md" shape="square" tone="danger" onClick={onRemove} />
  );

  return (
    <div className={clsx(styles.editRow, gridClassName)}>
      {leading}

      {/* Mobile view: the body IS the edit affordance (tap it). Desktop, and edit mode everywhere, is a plain
          `display: contents` wrapper so instrument/mic (or label/value) stay their own grid columns. */}
      {!editing && compact ? (
        <button type="button" className={clsx(styles.editBody, styles.editBodyTap)}
          aria-label={editLabel} onClick={onEdit}>
          {body}
        </button>
      ) : (
        <span className={styles.editBody}>{body}</span>
      )}

      {!compact ? (
        // DESKTOP: aside (status/tier), then pencil (view) / check (edit), then the trashcan — inline grid cols.
        <>
          {aside}
          <span className={clsx(styles.editCtl, editing && styles.editCtlDone)}>
            {editing ? (
              <IconButton ref={editBtnRef} icon={Check} label="Done editing" size="md" shape="square" onClick={onDone} />
            ) : (
              <IconButton ref={editBtnRef} icon={Pencil} label={editLabel} size="md" shape="square" onClick={onEdit} />
            )}
          </span>
          {remove}
        </>
      ) : editing ? (
        // MOBILE, editing: a footer on its own row — delete on the left, cancel + done on the right.
        <div className={styles.editFoot}>
          {remove}
          <span className={styles.editFootSpacer} />
          <IconButton icon={X} label={cancelLabel} size="md" shape="square" onClick={onCancel} />
          <span className={clsx(styles.editCtl, styles.editCtlDone)}>
            <IconButton ref={editBtnRef} icon={Check} label="Done editing" size="md" shape="square" onClick={onDone} />
          </span>
        </div>
      ) : (
        // MOBILE, view: the per-row control (status/tier) + the trashcan, as ONE right-weighted cluster — it
        // stays at the trailing edge, beside the summary on one row (`margin-inline-start: auto`); a long
        // summary truncates with an ellipsis rather than pushing this cluster to its own line.
        <div className={styles.editViewTrail}>
          {aside}
          {remove}
        </div>
      )}
    </div>
  );
}

/**
 * ONE input-list channel (SME TASK-017: "a single pill"). The reader (`!controls`) is a view-only pill; the
 * editor renders the shared <EditableRow> with the channel's slots — # (leading), instrument/mic (summary vs
 * fields), the status pill (aside). Shared by editor + reader so the two cannot drift.
 *
 * The channel number is POSITIONAL (`index + 1`) and never stored — the server derives `channel_no` from the
 * PUT's array order. The status control is a BUTTON, not a select (Ryan: "just a button you can click").
 */
function ChannelRow(props: {
  index: number;
  instrument: string;
  mic: string;
  status: string | null | undefined;
  /** Mobile shell — the status pill reads the short TRAVEL / NEED label; the accessible name stays full. */
  compact?: boolean;
  /** Present ⇒ this is the editor's row (the reader passes none). */
  controls?: {
    editing: boolean;
    micOptions: readonly MicOption[];
    onChange: (changes: { instrument?: string; mic?: string; micId?: string | null; status?: string }) => void;
    onStartEdit: () => void;
    onDoneEdit: () => void;
    onCancelEdit: () => void;
    onRemove: () => void;
  };
}) {
  const { index, instrument, mic, status, controls, compact } = props;
  const n = index + 1;

  // Hooks run unconditionally (before the reader early-return). `editBtnRef` is the focus target the row hands
  // the caret to on commit — see EditableRow. `instRef` puts the caret in a freshly opened row.
  const editBtnRef = useRef<HTMLButtonElement>(null);
  const instRef = useRef<HTMLInputElement>(null);
  const editing = controls?.editing ?? false;
  useEffect(() => { if (editing) instRef.current?.focus(); }, [editing]);

  const chip = clsx(
    styles.ilToggle,
    status === "traveling_with" ? styles.ilToggleTravel : status === "need" ? styles.ilToggleNeed : styles.ilToggleUnset,
  );
  const interactive = !!controls;
  const fullStatus = statusText(status, { interactive });
  const shortStatus = status === "traveling_with" ? "Travel" : status === "need" ? "Need" : null;
  const visibleStatus = compact && shortStatus ? shortStatus : fullStatus;

  const leading = <span className={styles.ilCh}>{n}</span>;
  const summary = (
    <>
      <span className={styles.ilInst}>{instrument || "—"}</span>
      <span className={styles.ilMic} title={mic || undefined}>{mic || "—"}</span>
    </>
  );

  // READER: a view-only pill (no edit affordances, no venue-token). Same panel + body as the editor, minus
  // the controls, so the two cannot drift.
  if (!controls) {
    return (
      <div className={clsx(styles.editRow, styles.ilRowRo)}>
        {leading}
        <span className={styles.editBody}>{summary}</span>
        <span className={chip}>{visibleStatus}</span>
      </div>
    );
  }

  // Focus handoff + keyboard, owned here (the caller wires Enter/Escape on its own fields).
  const endEdit = (done: () => void) => { editBtnRef.current?.focus(); done(); };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!controls.editing) return;
    if (e.key === "Enter") { e.preventDefault(); endEdit(controls.onDoneEdit); }
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); endEdit(controls.onCancelEdit); }
  };

  const fields = (
    <>
      <Field ref={instRef} className={styles.ilField} label={`Channel ${n} instrument`} hideLabel
        value={instrument} placeholder="Instrument (e.g. Kick)" onKeyDown={onKeyDown}
        onChange={(e) => controls.onChange({ instrument: e.target.value })} />
      {/* NOT a `<datalist>` — see MicSuggest's docblock. Its popup portals. */}
      <MicSuggest className={styles.ilField} value={mic} options={controls.micOptions} label={`Channel ${n} mic`}
        placeholder="Mic (e.g. Shure Beta 52)" onKeyDown={onKeyDown}
        onChange={(next) => controls.onChange(next)} />
    </>
  );
  const statusPill = (
    <button type="button" className={chip}
      aria-pressed={status === "traveling_with"}
      aria-label={`Channel ${n} status: ${fullStatus}`}
      onClick={() => controls.onChange({ status: nextStatus(status) })}>
      {visibleStatus}
    </button>
  );

  return (
    <EditableRow
      compact={!!compact} editing={editing} gridClassName={styles.ilGrid}
      editLabel={`Edit channel ${n}`} cancelLabel={`Cancel editing channel ${n}`} removeLabel={`Remove channel ${n}`}
      editBtnRef={editBtnRef}
      onEdit={controls.onStartEdit}
      onDone={() => endEdit(controls.onDoneEdit)}
      onCancel={() => endEdit(controls.onCancelEdit)}
      onRemove={controls.onRemove}
      leading={leading} aside={statusPill} summary={summary}>
      {fields}
    </EditableRow>
  );
}

/**
 * ONE hospitality/rider item, on the SAME shared <EditableRow> as the input list (SME TASK-020, and ART-188's
 * consistency pass): a view/edit pill — summary = label · value, aside = the Essential/Full tier, the standard
 * trashcan delete + (mobile) cancel/done footer. No leading number and no reader path (the read-only rider is
 * grouped by tier elsewhere).
 */
function RiderRow(props: {
  index: number;
  label: string;
  value: string;
  tier: RiderTier;
  compact: boolean;
  editing: boolean;
  onChange: (changes: Partial<{ label: string; value: string; tier: RiderTier }>) => void;
  onStartEdit: () => void;
  onDoneEdit: () => void;
  onCancelEdit: () => void;
  onRemove: () => void;
}) {
  const { index, label, value, tier, compact, editing, onChange, onStartEdit, onDoneEdit, onCancelEdit, onRemove } = props;
  const n = index + 1;

  const editBtnRef = useRef<HTMLButtonElement>(null);
  const labelRef = useRef<HTMLInputElement>(null);
  useEffect(() => { if (editing) labelRef.current?.focus(); }, [editing]);
  const endEdit = (done: () => void) => { editBtnRef.current?.focus(); done(); };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (!editing) return;
    if (e.key === "Enter") { e.preventDefault(); endEdit(onDoneEdit); }
    if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); endEdit(onCancelEdit); }
  };

  const summary = (
    <>
      <span className={styles.ilInst}>{label || "—"}</span>
      <span className={styles.ilMic} title={value || undefined}>{value || "—"}</span>
    </>
  );
  const fields = (
    <>
      <Field ref={labelRef} className={styles.ilField} label={`Rider ${n} item`} hideLabel value={label}
        placeholder="Label (e.g. Towels)" onKeyDown={onKeyDown}
        onChange={(e) => onChange({ label: e.target.value })} />
      <Field className={styles.ilField} label={`Rider ${n} detail`} hideLabel value={value}
        placeholder="Value (e.g. 12 black bath towels)" onKeyDown={onKeyDown}
        onChange={(e) => onChange({ value: e.target.value })} />
    </>
  );
  const tierToggle = (
    <RiderTierToggle tier={tier} rowLabel={label || `Rider ${n}`} onChange={(t) => onChange({ tier: t })} />
  );

  return (
    <EditableRow
      compact={compact} editing={editing} gridClassName={styles.riderGrid}
      editLabel={`Edit rider item ${n}`} cancelLabel={`Cancel editing rider item ${n}`} removeLabel={`Remove rider row ${n}`}
      editBtnRef={editBtnRef}
      onEdit={onStartEdit}
      onDone={() => endEdit(onDoneEdit)}
      onCancel={() => endEdit(onCancelEdit)}
      onRemove={onRemove}
      aside={tierToggle} summary={summary}>
      {fields}
    </EditableRow>
  );
}

type FileDraft = {
  fileId: string | null; category: string; mediaId: string; label: string;
  visibleToVenue: boolean; mediaKind: string; fileName: string | null; sizeBytes: number | null;
};
/** `cid` is a CLIENT-ONLY row identity, never sent and never read back — the server derives `channel_no`
 *  from array order, so the rows have no stable server key to aim an editor at. It exists because keying
 *  the open inline editor by array INDEX is unsafe: the save filters out channels with an empty instrument
 *  and `setD(toDraft(saved))` then replaces the array with the shorter server echo, shifting every index
 *  after the dropped row — an index-keyed editor would land on a different channel and its Escape snapshot
 *  would overwrite it. Matching on `cid` cannot be aimed at the wrong row by any filter or reorder. */
type ChannelDraft = { cid: string; instrument: string; mic: string; micId: string | null; status: string; notes: string };

let cidSeq = 0;
const newCid = () => `ch-${++cidSeq}`;

/** `tier` is never empty in a draft — a row read without one is an essential, which is what it was. */
type RiderTier = "essential" | "full";
/** `cid` is the client-only row identity the inline editor aims at — same reason as `ChannelDraft.cid`: the
 *  save filters out empty-label rows, so an index-keyed editor could land on a different row after the echo. */
type RiderDraft = { cid: string; label: string; value: string; tier: RiderTier };
type Draft = {
  files: FileDraft[];
  channels: ChannelDraft[];
  techNotes: string;
  rider: RiderDraft[];
  contactName: string;
  contactEmail: string;
  contactPhone: string;
};

/**
 * Is the draft unchanged since it was loaded or last saved? Compared FIELD-WISE with `cid` stripped, because `cid`
 * is a client-only row identity minted fresh by every `toDraft` call — a plain deep-equal against a re-derived
 * draft would report "dirty" for a document nobody touched, and the switch prompt would then fire every time.
 */
function sameDraft(a: Draft, b: Draft): boolean {
  const strip = (d: Draft) => JSON.stringify({
    ...d,
    channels: d.channels.map(({ cid: _cid, ...rest }) => rest),
    rider: d.rider.map(({ cid: _cid, ...rest }) => rest),
  });
  return strip(a) === strip(b);
}

function toDraft(read: ProductionRead): Draft {
  // The generated schema marks every field optional/nullable (Swashbuckle nullable→optional), so coalesce to the
  // non-nullable Draft shape.
  return {
    files: (read.files ?? []).map((f) => ({
      fileId: f.fileId ?? null, category: f.category ?? "foh_preset", mediaId: f.mediaId ?? "", label: f.label ?? "",
      visibleToVenue: f.visibleToVenue ?? true, mediaKind: f.mediaKind ?? "other",
      fileName: f.originalFilename ?? null, sizeBytes: f.sizeBytes ?? null,
    })),
    channels: (read.channels ?? []).map((c) => ({
      cid: newCid(),
      instrument: c.instrument ?? "", mic: c.micLabel ?? c.micCustom ?? "", micId: c.micId ?? null,
      status: c.status ?? "", notes: c.notes ?? "",
    })),
    techNotes: read.notes?.techNotes ?? "",
    rider: (read.notes?.riderEssentials ?? []).map((r) => ({
      cid: newCid(),
      label: r.label ?? "", value: r.value ?? "", tier: r.tier === "full" ? "full" : "essential",
    })),
    contactName: read.notes?.productionContactName ?? "",
    contactEmail: read.notes?.productionContactEmail ?? "",
    contactPhone: read.notes?.productionContactPhone ?? "",
  };
}

function ProductionEditor(props: {
  initial: ProductionRead;
  lookId: LookId;
  onSelectLook: (lookId: LookId) => void;
  /** The header switcher calls through this (ART-251), so a switch from outside the editor still meets the
   *  unsaved-draft prompt. Registered on every render (it closes over the current draft), cleared on unmount. */
  guardRef: MutableRefObject<((to: LookId) => void) | null>;
}) {
  const { initial, lookId, onSelectLook, guardRef } = props;
  // Mobile shell (< lg): cut the card-title sub-lines and collapse the six file slots into one "Files" group.
  // The populated tab runs ~4 screens even empty — six file cards, each a full card wrapping one dropzone — so
  // the reference groups them (a band touches files once a tour; the venue link + input list are the live
  // surfaces). Desktop keeps the two-column split (input list beside the file rail). (ART-188)
  const compact = usePhoneLayout();
  const save = useUpdateProduction(lookId);
  const micsQ = useMics();
  // The band's persistent custom mics (ART-166): merged AHEAD of the catalog below, and topped up on save.
  const customMicsQ = useBandCustomMics();
  const bankMic = useBankCustomMic();
  const { uploadMedia } = useUploadMedia();
  const { download } = useMediaDownload();
  const [d, setD] = useState<Draft>(() => toDraft(initial));
  // The draft as last SAVED (or as loaded). Switching advance discards unsaved edits, so the switch is guarded
  // against this rather than against `initial` — which never changes while the editor is mounted.
  const [baseline, setBaseline] = useState<Draft>(d);
  // A pending switch, held while the user answers the unsaved-changes prompt.
  const [pendingLook, setPendingLook] = useState<{ to: LookId } | null>(null);

  // WHICH advance the current draft was seeded from. Because the pack is held across a switch
  // (`keepPreviousData`), `initial` is briefly the PREVIOUS look's data — so the reseed keys on the pack's own
  // echoed `lookId`, not on the selection. That echo exists for exactly this: it is the only way to tell "the
  // advance I asked for has arrived" from "I am still looking at the last one".
  const packLook: LookId = initial.lookId ?? null;
  const seededFor = useRef<LookId>(packLook);
  useEffect(() => {
    if (seededFor.current === packLook) return;
    seededFor.current = packLook;
    const next = toDraft(initial);
    setD(next);
    setBaseline(next);
    setEditing(null);
  }, [packLook, initial]);

  // True while the selected advance's pack is still in flight and the previous one is on screen.
  const stale = packLook !== lookId;

  const dirty = !sameDraft(d, baseline);

  // INTERIM, and deliberately so: this prompt exists because the page saves as one whole document behind a single
  // header pill. Live save is the intended direction, and when it lands this guard should go with it rather than
  // be carried forward — a per-field save has nothing to lose on a switch. Blocking is the smallest correct
  // behaviour until then; silently discarding a draft is the one option that is clearly wrong.
  const requestLook = (to: LookId) => {
    if (to === lookId) return;
    if (dirty) { setPendingLook({ to }); return; }
    onSelectLook(to);
  };
  guardRef.current = requestLook;
  useEffect(() => () => { guardRef.current = null; }, [guardRef]);
  // A single hidden file input, retargeted per category via the ref (avoids seven inputs). `uploadingCat` disables the
  // triggering card + labels its spinner; other cards stay live.
  const [uploadingCat, setUploadingCat] = useState<string | null>(null);
  // Which input-list row (if any) is open for inline edit, plus the pre-edit values Escape restores.
  // Keyed by the row's client id, NOT its index — see `ChannelDraft.cid`.
  const [editing, setEditing] = useState<{ cid: string; snapshot: ChannelDraft } | null>(null);
  // The same, for the rider list — which item is open for inline edit, plus the snapshot cancel restores.
  const [editingRider, setEditingRider] = useState<{ cid: string; snapshot: RiderDraft } | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const pendingCat = useRef<string>("foh_preset");

  const set = (changes: Partial<Draft>) => setD((prev) => ({ ...prev, ...changes }));

  const catalog: MicView[] = micsQ.data ?? [];

  // The suggestion pool: the band's own custom mics FIRST (ART-166 — "your own gear beats the stock list"),
  // then the global catalog (each carrying its authoritative micId). The customs are the band's PERSISTENT
  // list plus this pack's saved `mic_custom` values, de-duplicated case-insensitively, and NEVER anything the
  // catalog already carries (the catalog's authoritative micId wins — a picked label resolves to its id on
  // save). Not the live draft — reading it would feed each keystroke straight back as a suggestion.
  const micOptions: MicOption[] = (() => {
    const catalogByLabel = new Map<string, MicOption>();
    for (const m of catalog) if (m.label) catalogByLabel.set(m.label, { label: m.label, micId: m.micId ?? null });
    const catalogLower = new Set([...catalogByLabel.keys()].map((l) => l.toLowerCase()));

    const seen = new Set<string>();
    const customs: MicOption[] = [];
    const addCustom = (raw: string | null | undefined) => {
      const v = (raw ?? "").trim();
      if (!v) return;
      const key = v.toLowerCase();
      if (catalogLower.has(key) || seen.has(key)) return;
      seen.add(key);
      customs.push({ label: v, micId: null });
    };
    for (const label of customMicsQ.data ?? []) addCustom(label);
    for (const c of initial.channels ?? []) addCustom(c.micCustom);

    customs.sort((a, b) => a.label.localeCompare(b.label));
    const catalogOpts = [...catalogByLabel.values()].sort((a, b) => a.label.localeCompare(b.label));
    return [...customs, ...catalogOpts];
  })();

  // Update / remove a file by its index in the flat draft array (category cards render a filtered view but edit the
  // authoritative array so nothing else shifts).
  const updateFile = (i: number, changes: Partial<FileDraft>) =>
    set({ files: d.files.map((x, idx) => (idx === i ? { ...x, ...changes } : x)) });
  const removeFile = (i: number) => set({ files: d.files.filter((_, idx) => idx !== i) });

  const triggerUpload = (category: string) => {
    pendingCat.current = category;
    fileInput.current?.click();
  };

  const onPickFiles = async (files: FileList | null) => {
    const category = pendingCat.current;
    if (!files || files.length === 0) return;
    setUploadingCat(category);
    try {
      const added: FileDraft[] = [];
      for (const file of Array.from(files)) {
        const kind = mediaKindForFile(file);
        const asset = await uploadMedia(file, { kind, purpose: "production" });
        added.push({
          fileId: null, category, mediaId: asset.mediaId!,
          label: "", visibleToVenue: true, mediaKind: asset.kind ?? kind,
          fileName: file.name, sizeBytes: file.size,
        });
      }
      setD((prev) => ({ ...prev, files: [...prev.files, ...added] }));
      pushToast(added.length === 1 ? "File uploaded — remember to save." : `${added.length} files uploaded — remember to save.`, "success");
    } catch (e) {
      pushToast(errorMessage(e, "Couldn't upload the file."), "error");
    } finally {
      setUploadingCat(null);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const onDownload = (mediaId: string, filename: string | null | undefined) =>
    void download(mediaId, filename).catch((e) => pushToast(errorMessage(e, "Couldn't download the file."), "error"));

  const resolveMic = (mic: string): { micId: string | null; micCustom: string | null } => {
    const v = mic.trim();
    if (!v) return { micId: null, micCustom: null };
    const match = catalog.find((m) => m.label === v);
    return match ? { micId: match.micId ?? null, micCustom: null } : { micId: null, micCustom: v };
  };

  const onSave = () => {
    save.mutate(
      {
        files: d.files.map((f) => ({
          fileId: f.fileId, category: f.category, mediaId: f.mediaId,
          label: f.label.trim() || null, visibleToVenue: f.visibleToVenue,
        })),
        channels: d.channels
          .filter((c) => c.instrument.trim())
          .map((c) => ({
            instrument: c.instrument.trim(),
            // An UNCHANGED catalog selection keeps its authoritative micId (never re-resolved against the catalog),
            // so a slow/failed useMics can't downgrade it to a custom; only user-EDITED text (micId cleared) re-resolves.
            ...(c.micId ? { micId: c.micId, micCustom: null } : resolveMic(c.mic)),
            status: c.status || null,
            notes: c.notes.trim() || null,
          })),
        notes: {
          techNotes: d.techNotes.trim() || null,
          riderEssentials: d.rider
            .map((r) => ({ label: r.label.trim(), value: r.value.trim() || null, tier: r.tier }))
            .filter((r) => r.label),
          productionContactName: d.contactName.trim() || null,
          productionContactEmail: d.contactEmail.trim() || null,
          productionContactPhone: d.contactPhone.trim() || null,
        },
      },
      {
        // Close the inline editor with the draft it was editing: `toDraft` mints fresh `cid`s, so a row
        // held open across the save would be pointing at a draft that no longer exists.
        onSuccess: (saved) => {
          setEditing(null);
          const next = toDraft(saved);
          setD(next);
          setBaseline(next);        // the saved state is the new "clean" — otherwise the switch guard stays armed forever
          // Bank any free-typed mic that just landed as `mic_custom` so it autocompletes next time (ART-166).
          // The POST is idempotent server-side (a catalog/own-list/too-short label is a no-op); we still de-dupe
          // against what we already hold to avoid pointless calls.
          const known = new Set((customMicsQ.data ?? []).map((l) => l.toLowerCase()));
          const toBank = new Set<string>();
          for (const c of saved.channels ?? []) {
            const v = (c.micCustom ?? "").trim();
            if (v.length >= 3 && !known.has(v.toLowerCase())) toBank.add(v);
          }
          for (const label of toBank) bankMic.mutate(label);
          pushToast("Production pack saved.", "success");
        },
        onError: (e) => pushToast(errorMessage(e, "Couldn't save your production pack."), "error"),
      },
    );
  };

  // ONE document save; the pill rides the editable card headers (the EPK convention — COMPONENTS.md).
  // Blocked until the mic catalog loads so a typed catalog name is never re-resolved against an empty
  // catalog and silently downgraded to a custom mic (unchanged selections keep their micId).
  const saveBtn = (
    <Button variant="ghost" size="xs"
      onClick={onSave}
      // `stale` blocks the window between picking an advance and its pack arriving: the draft still belongs to
      // the PREVIOUS look, and the PUT is addressed to the new one, so a save there would copy one advance over
      // another.
      disabled={save.isPending || uploadingCat !== null || micsQ.isLoading || stale}
    >
      {save.isPending ? "Saving…" : micsQ.isLoading ? "Loading catalog…" : stale ? "Loading…" : "Save"}
    </Button>
  );

  // `cardHandlers` bundles the file ops for the legacy rider-doc chips below.
  const cardHandlers = { triggerUpload, updateFile, removeFile, onDownload, uploadingCat };

  // `rider_full` files a band uploaded BEFORE the tier retired that upload. They still render and still
  // download; the index is carried because `removeFile` addresses the draft's flat file array by position.
  const legacyRiderDocs = d.files.map((f, i) => ({ f, i })).filter(({ f }) => f.category === "rider_full");

  return (
    <>
      {pendingLook && (
        <AcDialog
          title="Discard unsaved changes?"
          message="This advance has edits you haven't saved. Switching will lose them."
          confirmLabel="Discard and switch"
          cancelLabel="Keep editing"
          tone="danger"
          onCancel={() => setPendingLook(null)}
          onConfirm={() => { const to = pendingLook.to; setPendingLook(null); onSelectLook(to); }}
        />
      )}

      {/* The single retargeted upload input (one for every category card). */}
      <input ref={fileInput} type="file" multiple className={styles.hiddenInput}
        onChange={(e) => void onPickFiles(e.target.files)} />

      {/* Two columns top to bottom (ART-251): the input list / tech notes / hospitality in the main column; the
          production contact over the additional files in the side column. Below 1180px it is one column with the
          contact FIRST — the grid areas place it, so the DOM order is free to read contact → main → files. */}
      <div className={styles.prodSplit}>
        <div className={styles.prodSplitContact}>
          <ContactCard
            contactName={d.contactName} contactEmail={d.contactEmail} contactPhone={d.contactPhone}
            onContact={(changes) => set(changes)}
            saveBtn={saveBtn}
          />
        </div>
        <div className={styles.prodSplitMain}>
          <Card id="prod-input" title="Input list" actions={<>{saveBtn}</>}>

            {/* Capped + scrollable (SME TASK-018), at its required height. The Add row below sits OUTSIDE the
                scroll box so it never scrolls out of reach. No empty-state line: the editor only mounts on
                resolved data, so an empty list is unambiguously empty. */}
            <div className={styles.inputList}>
              {d.channels.map((c, i) => (
                <ChannelRow key={c.cid} index={i} instrument={c.instrument} mic={c.mic} status={c.status}
                  compact={compact}
                  controls={{
                    editing: editing?.cid === c.cid,
                    micOptions,
                    onChange: (changes) => set({ channels: d.channels.map((x) => (x.cid === c.cid ? { ...x, ...changes } : x)) }),
                    onStartEdit: () => setEditing({ cid: c.cid, snapshot: c }),
                    onDoneEdit: () => setEditing(null),
                    onCancelEdit: () => {
                      const snap = editing?.snapshot;
                      // Cancelling a brand-new row (one that was never given an instrument) REMOVES it rather
                      // than leaving a blank row behind — "I added a channel by mistake, cancel" should undo the
                      // add. An existing row (its snapshot carries an instrument) is restored to that snapshot.
                      if (snap && !snap.instrument.trim()) set({ channels: d.channels.filter((x) => x.cid !== c.cid) });
                      else if (snap) set({ channels: d.channels.map((x) => (x.cid === c.cid ? snap : x)) });
                      setEditing(null);
                    },
                    onRemove: () => { setEditing(null); set({ channels: d.channels.filter((x) => x.cid !== c.cid) }); },
                  }} />
              ))}
            </div>
            {/* Add sits BELOW the list, after the bottom row. A blank row opens in edit mode so there is a
                field to type — otherwise "Add channel" would append an empty pill with nothing to type into. */}
            {d.channels.length < MAX_CHANNELS && (
              <AddRow
                onClick={() => {
                  const row: ChannelDraft = { cid: newCid(), instrument: "", mic: "", micId: null, status: "", notes: "" };
                  setEditing({ cid: row.cid, snapshot: row });
                  set({ channels: [...d.channels, row] });
                }}>
                Add channel
              </AddRow>
            )}
          </Card>

          {/* Tech notes, then Hospitality — stacked BENEATH the input list in the split's left column, so the
              input list keeps its own (required) height instead of stretching to the preset rail. */}
          <Card id="prod-notes" title="Tech notes" actions={<>{saveBtn}</>}>

        <Textarea label="Tech notes" hideLabel rows={4} value={d.techNotes} placeholder="Tech requirements, special notes, gotchas…"
          onChange={(e) => set({ techNotes: e.target.value })} />
      </Card>

      {/* Hospitality / rider — ONE tagged line-item list. The full rider is not a document: it is this list
          read whole, with the essentials tagged. */}
      <Card id="prod-rider" title="Hospitality / rider" actions={<>{saveBtn}</>}>

        {/* No "Rider essentials" sub-label — the card is already titled "Hospitality / rider". Each item is the
            shared EditableRow (same view/edit + card + control set as the input list), summary = label · value,
            aside = the Essential/Full tier. Add sits BELOW the list; no empty-state line (the editor only mounts
            on resolved data, so an empty list is unambiguously empty). */}
        {d.rider.map((r, i) => (
          <RiderRow key={r.cid} index={i} label={r.label} value={r.value} tier={r.tier} compact={compact}
            editing={editingRider?.cid === r.cid}
            onChange={(changes) => set({ rider: d.rider.map((x) => (x.cid === r.cid ? { ...x, ...changes } : x)) })}
            onStartEdit={() => setEditingRider({ cid: r.cid, snapshot: r })}
            onDoneEdit={() => setEditingRider(null)}
            onCancelEdit={() => {
              const snap = editingRider?.snapshot;
              // Cancelling a brand-new (never-labelled) row removes it; an existing row restores its snapshot.
              if (snap && !snap.label.trim()) set({ rider: d.rider.filter((x) => x.cid !== r.cid) });
              else if (snap) set({ rider: d.rider.map((x) => (x.cid === r.cid ? snap : x)) });
              setEditingRider(null);
            }}
            onRemove={() => { setEditingRider(null); set({ rider: d.rider.filter((x) => x.cid !== r.cid) }); }} />
        ))}
        {/* Add sits BELOW the list, after the bottom item. A new row is an ESSENTIAL, opened straight into edit
            (like Add channel) so there is a field to type. */}
        {d.rider.length < MAX_RIDER && (
          <AddRow onClick={() => {
            const row: RiderDraft = { cid: newCid(), label: "", value: "", tier: "essential" };
            setEditingRider({ cid: row.cid, snapshot: row });
            set({ rider: [...d.rider, row] });
          }}>
            Add item
          </AddRow>
        )}
        {/* Legacy rider DOCUMENTS still render and still download — there is just no way to add another (the
            tagged list above IS the full rider). Read-only ON PURPOSE: do not re-add the upload. */}
        {legacyRiderDocs.length > 0 && (
          <>
            <p className={styles.vlabelSub}>Full rider document (legacy)</p>
            {legacyRiderDocs.map(({ f, i }) => (
              <div key={f.mediaId || i} className={styles.fileChip}>
                <Icon icon={FileText} size={16} className={styles.fileChipIcon} />
                <div className={styles.fileChipMain}>
                  <p className={styles.fileChipName} title={f.label || f.fileName || "Full rider"}>{f.label || f.fileName || "Full rider"}</p>
                  <p className={styles.fileChipMeta}>{fileMeta(f)}</p>
                </div>
                {f.visibleToVenue && <span className={styles.venueTag}>On venue page</span>}
                {f.mediaId && (
                  <button type="button" className={styles.chipBtn} aria-label={`Download ${f.label || f.fileName || "Full rider"}`}
                    onClick={() => cardHandlers.onDownload(f.mediaId, f.fileName ?? f.label)}>
                    <Icon icon={Download} size={16} />
                  </button>
                )}
                <button type="button" className={styles.chipDanger} aria-label={`Remove ${f.label || f.fileName || "Full rider"}`}
                  onClick={() => cardHandlers.removeFile(i)}>
                  <Icon icon={Trash2} size={16} />
                </button>
              </div>
            ))}
          </>
        )}
          </Card>
        </div>
        {/* "Additional files" — ONE card for the six production-file categories (was six separate cards, the
            preset/backup rail). The Add control is a dropdown of the categories; uploaded files land grouped
            by category, each section under a mono header. Same card on every shell (the split is a single
            column below 1180px, so this simply stacks under the input list / tech notes / hospitality). */}
        <div className={styles.prodSplitSide}>
          <Card id="prod-files" title="Additional files" actions={<>{saveBtn}</>}>
            {[...PRESET_CATEGORIES, STAGE_PLOT].map((cat) => {
              const rows = d.files.map((f, i) => ({ f, i })).filter(({ f }) => f.category === cat.value);
              if (rows.length === 0) return null;   // only a section that HAS rows gets a header
              return (
                <div key={cat.value} className={styles.addlSection}>
                  <p className={styles.addlSectionHead}>{cat.title}</p>
                  {rows.map(({ f, i }) => (
                    <div key={f.mediaId || i} className={styles.fileChip}>
                      <Icon icon={FileText} size={16} className={styles.fileChipIcon} />
                      <div className={styles.fileChipMain}>
                        <input className={styles.chipLabelInput} value={f.label} placeholder={f.fileName ?? "Label"}
                          aria-label={`${cat.title} label`} onChange={(e) => updateFile(i, { label: e.target.value })} />
                        <p className={styles.fileChipMeta}>{fileMeta(f)}</p>
                      </div>
                      <label className={styles.venueToggle} title="Show on the public venue page">
                        <input type="checkbox" checked={f.visibleToVenue} onChange={(e) => updateFile(i, { visibleToVenue: e.target.checked })} />
                        Venue
                      </label>
                      {f.mediaId && (
                        <button type="button" className={styles.chipBtn} aria-label={`Download ${f.label || f.fileName || cat.title}`}
                          onClick={() => onDownload(f.mediaId, f.fileName ?? f.label)}>
                          <Icon icon={Download} size={16} />
                        </button>
                      )}
                      <button type="button" className={styles.chipDanger} aria-label={`Remove ${f.label || f.fileName || cat.title}`}
                        onClick={() => removeFile(i)}>
                        <Icon icon={Trash2} size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              );
            })}
            {/* The add control: a dropdown of the categories, at the bottom like the other lists' Add rows.
                Picking one opens the file browser scoped to that category (`triggerUpload`). `value` stays "" —
                it is an action, not a stored selection. */}
            <Picker
              label="Add a file"
              bare
              value=""
              placeholder={uploadingCat ? "Uploading…" : "+ Add a file"}
              triggerClassName={styles.addFileBtn}
              disabled={uploadingCat !== null}
              onChange={(v) => triggerUpload(v)}
              options={[...PRESET_CATEGORIES, STAGE_PLOT].map((c) => ({ value: c.value, label: c.title }))}
            />
          </Card>
        </div>
      </div>

      {/* Save lives as the header pill (COMPONENTS.md). It stays blocked until the mic catalog has
          loaded, so a freshly-typed catalog name can never be re-resolved against an empty catalog and
          silently downgraded to a custom mic (unchanged selections keep their micId). */}
    </>
  );
}

// ---- helpers --------------------------------------------------------------------------------------------------

function fileMeta(f: FileDraft): string {
  const parts: string[] = [];
  if (f.fileName) parts.push(f.fileName);
  if (typeof f.sizeBytes === "number" && f.sizeBytes > 0) parts.push(formatSize(f.sizeBytes));
  return parts.join(" · ");
}

function roFileMeta(f: { originalFilename?: string | null; sizeBytes?: number | null }): string {
  const parts: string[] = [];
  if (f.originalFilename) parts.push(f.originalFilename);
  if (typeof f.sizeBytes === "number" && f.sizeBytes > 0) parts.push(formatSize(f.sizeBytes));
  return parts.join(" · ");
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}


